import Api from "@/config/Api";

// In-flight dedup map — keyed by period.
// Allows simultaneous component calls to share ONE in-flight HTTP request,
// but never caches the result across calls (prevents stale data after backend updates).
const _pendingRequests = {};

/**
 * Starts (or joins) a single in-flight GET /me/dashboard/summary request.
 * Once the request settles, the entry is removed — no persistent cache.
 */
function ensureSummary(period) {
  if (!_pendingRequests[period]) {
    _pendingRequests[period] = Api()
      .get("/me/dashboard/summary", { params: { period } })
      .then(res => {
        delete _pendingRequests[period];
        return res.data?.success ? res.data.data : null;
      })
      .catch(() => {
        delete _pendingRequests[period];
        return null;
      });
  }
  return _pendingRequests[period];
}

export default {
  /**
   * Call this before child components mount to warm the cache.
   * Returns the same promise any in-flight request for this period will share.
   */
  prefetchSummary(period = "month") {
    return ensureSummary(period);
  },

  /**
   * No-op — cache eliminado. Se preserva para compatibilidad con llamadas existentes.
   */
  invalidateSummary() {},

  /**
   * Cancela requests en vuelo al cambiar de business.
   */
  clearAll() {
    for (const k of Object.keys(_pendingRequests)) delete _pendingRequests[k];
  },

  // ─── Bundled methods (backed by summary endpoint) ───────────────────────────

  async getKpis(options = {}) {
    const period = options?.params?.period || "month";
    const summary = await ensureSummary(period);
    if (summary?.kpis) {
      return { data: { success: true, data: summary.kpis } };
    }
    return Api().get("/me/dashboard/kpis", options);
  },

  async getFunnel(options = {}) {
    const period = options?.params?.period || "month";
    const summary = await ensureSummary(period);
    if (summary?.funnel) {
      return { data: { success: true, data: summary.funnel } };
    }
    return Api().get("/me/dashboard/funnel", options);
  },

  async getRecentQuotes(options = {}) {
    // recent_quotes is period-independent — join any in-flight summary request
    const pending = Object.values(_pendingRequests)[0];
    if (pending) {
      const summary = await pending;
      if (summary?.recent_quotes) {
        return { data: { success: true, data: summary.recent_quotes } };
      }
    }
    return Api().get("/me/dashboard/recent-quotes", options);
  },

  async getTopClients(options = {}) {
    const period = options?.params?.period || "month";
    const summary = await ensureSummary(period);
    if (summary?.top_clients) {
      return { data: { success: true, data: summary.top_clients } };
    }
    return Api().get("/me/dashboard/top-clients", options);
  },

  // ─── Individual methods (not bundled) ───────────────────────────────────────

  getClientScoring(options = {}) {
    return Api().get("/me/dashboard/client-scoring", options);
  },

  getAlerts(options = {}) {
    return Api().get("/me/dashboard/alerts", options);
  },

  getTopProducts(options = {}) {
    return Api().get("/me/dashboard/top-products", options);
  },

  getExpiredQuotes(options = {}) {
    return Api().get("/me/dashboard/expired-quotes", options);
  },

  getDormantClients(options = {}) {
    return Api().get("/me/dashboard/dormant-clients", options);
  },

  getOverdueInvoices(options = {}) {
    return Api().get("/me/dashboard/overdue-invoices", options);
  },

  getPendingCollection(options = {}) {
    return Api().get("/me/dashboard/pending-collection", options);
  },

  getGoals(options = {}) {
    return Api().get("/me/dashboard/goals", options);
  },

  updateMonthlyGoal(targetValue) {
    return Api().put("/me/dashboard/goals/monthly-revenue", {
      target_value: targetValue
    });
  }
};
